import { html, css } from 'lit-element';
import { faSave, faDownload, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { differenceInDays } from 'date-fns';
import { saveAs }  from '../FileSaver';
import toUTCLocaleDateString from '../toUTCLocaleDateString';
import secondsToTimeString from '../secondsToTimeString';
import { BaseElement } from './BaseElement';
import { API, EVENT_TYPE_LOGIN_STATUS, LOGIN_STATUS_LOGGED_IN } from '../API';
import { SearchParams } from '../SearchParams';
import { DIALOG_TYPES } from './ModalDialog';
import * as GUID from '../GUID';
import { PatientProgressData } from '../PatientProgressData';
import { State } from '../State';

import './PatientAlerts';
import './ROMChart';
import './CPMUsageChart';
import './PainChart';
import './SatisfactionChart';
import './ExerciseChart';
import './WalkChart';
import './KneeProtocol';

const _api = Symbol();
const _state = Symbol();
const _loginStatusHandler = Symbol();
const _data = Symbol();

const KNEE_ROM_NEUTRAL = 10;

class PatientProgress extends BaseElement {
	constructor() {
		super();

		this[_loginStatusHandler] = () => {
			if(this.api.loginStatus === LOGIN_STATUS_LOGGED_IN) {
				this.getPatientProgress();
			}
			this.requestUpdate();
		};

		this.library.add(faSave, faDownload, faExclamationTriangle);
	}
	static get styles() {
		return [
			...BaseElement.styles,
			css`
				:host {
					max-width: 7.5in;
					margin: auto;
					display: block;
				}
				.row {
					display: flex;
					flex-flow: row wrap;
					justify-content: space-around;
					margin-bottom: 0.5em;
				}
				.row > * {
					flex: 1;
				}
				.header {
					border: var(--theme-border, px solid black);
					background: var(--theme-background, white);
					border-radius: 0.25em;
					padding: 0.25em;
					font-weight: bold;
				}
				.warning {
					border: var(--theme-border, px solid black);
					background-color: var(--theme-warning, red);
					border-radius: 0.25em;
					padding: 0.25em;
				}
				.card {
					flex: 1;
					margin: 0.25em;
					border: var(--theme-border, 1px solid black);
					border-radius: 0.25em;
					padding: 0.25em;
					min-width: 2.25in;
				}
				.card .title {
					font-size: 1.5em;
				}
				.card .name {
					display: inline-block;
					width: 8em;
				}
				.card .content {
					display: flex;
					flex-flow: row nowrap;
				}
				.card .data-point {
					font-size: 2.5em;
					flex: 1;
					text-align: center;
				}
				.card .data-point .label {
					text-align: center;
					font-size: 0.4em;
				}
				.buttons {
					justify-content: flex-end;
				}
				.buttons > * {
					flex: none;
				}
				@media print {
					.section {
						page-break-inside: avoid;
					}
				}
			`
		];
	}
	get api() {
		return this[_api];
	}
	set api(v) {
		if(v !== null && !(v instanceof API))
			throw new TypeError('api must be an instance of API.');
		this[_api] = v;
	}
	get state() {
		return this[_state];
	}
	set state(v) {
		if(v !== null && !(v instanceof State))
			throw new TypeError('state must be an instance of State.');
		this[_state] = v;
	}
	get data() {
		return this[_data];
	}
	set data(v) {
		if(v !== null && !(v instanceof PatientProgressData))
			throw new TypeError('data must be an instance of PatientProgressData.');
		this[_data] = v;
		this.requestUpdate();
	}
	firstUpdated(changedProperties) {
		super.firstUpdated(changedProperties);

		if(this.api.loggedIn) {
			this.requestUpdate();
			this.getPatientProgress();
		}
	}
	connectedCallback() {
		super.connectedCallback();

		this.emit('get-api', { callback: api => this.api = api });
		this.emit('get-state', { callback: state => this.state = state });
		
		this.api.on(EVENT_TYPE_LOGIN_STATUS, this[_loginStatusHandler]);
	}
	disconnectedCallback() {
		super.disconnectedCallback();

		this.api.off(null, this[_loginStatusHandler]);

		this.api = null;
		this.state = null;
	}
	async getPatientProgress() {
		const { orderId } = SearchParams.fromString(window.location.search).toJSON();
		const id = GUID.newGUID();
		try {
			this.emit('show-busy', id);
			this.data = await this.api.patientProgressData(orderId);

			this.emit('hide-busy', id);
		}
		catch(err) {
			this.emit('hide-busy', id);
			console.error(err);
			this.emit('show-dialog', {
				title: 'Patient Progress Error',
				message: err instanceof TypeError ? 'Failed to connect to data service.' : err.message,
				type: DIALOG_TYPES.DIALOG_TYPE_ERROR
			});
		}
	}
	async getPatientProgressPdf() {
		const id = GUID.newGUID();
		try {
			this.emit('show-busy', id);
			const { orderId } = SearchParams.fromString(window.location.search).toJSON();
			saveAs(await this.api.patientProgressPdf(orderId));
			this.emit('hide-busy', id);
		}
		catch(err) {
			this.emit('hide-busy', id);
			this.emit('show-dialog', {
				title: 'Patient Progress Download Error',
				message: err.message,
				type: DIALOG_TYPES.DIALOG_TYPE_ERROR
			});
		}
	}
	avgUsageAderence() {
		if(!this.data || !this.data.doctorKneeProtocol || !this.data.lastDay) return '???';

		const totalUsageTime = ([...this.data.cpmSessionDaily].reduce((total, cpmSessionDay) => total + cpmSessionDay.totalUsageTime, 0) || 0) / 60;
		const requiredUsageTime = this.data.doctorKneeProtocol.sessionsPerDay * this.data.doctorKneeProtocol.sessionDuration;
		
		return Math.min(
			100,
			Math.floor(totalUsageTime / this.postOpDay() / requiredUsageTime * 100)
		) || 0;
	}
	currentUsageAdherence() {
		if(!this.data || !this.data.doctorKneeProtocol || !this.data.lastDay) return '???';

		const cpmSessionDay = [...this.data.cpmSessionDaily].find(v => v.usageDate === this.data.lastDay);
		const totalUsageTime = (cpmSessionDay ? cpmSessionDay.totalUsageTime : 0) / 60;
		const requiredUsageTime = this.data.doctorKneeProtocol.sessionsPerDay * this.data.doctorKneeProtocol.sessionDuration;

		return Math.min(
			100,
			Math.floor(totalUsageTime / requiredUsageTime * 100)
		) || 0;
	}
	postOpDay() {
		if(!this.data) return 0;

		return differenceInDays(
			new Date(this.data.lastDay),
			new Date(this.data.firstDay)
		);
	}
	reportDate() {
		if(!this.data) return null;

		return toUTCLocaleDateString(new Date(this.data.lastDay));
	}
	currentMaxFlexion() {
		if(!this.data || !this.data.cpmSessionDaily.lastDay) return KNEE_ROM_NEUTRAL;
		return this.data.cpmSessionDaily.lastDay.maxFlexion;
	}
	currentMaxFlexionGoal() {
		if(!this.data || !this.data.doctorKneeProtocol || !this.data.cpmSessionDaily.lastDay) return '???';

		const percent = Math.floor((this.data.cpmSessionDaily.lastDay.maxFlexion - KNEE_ROM_NEUTRAL) / (this.data.doctorKneeProtocol.targetFlexion - KNEE_ROM_NEUTRAL) * 100);
		
		return Math.max(Math.min(percent, 100), 0).toString();
	}
	currentMaxExtension() {
		if(!this.data || !this.data.cpmSessionDaily.lastDay) return KNEE_ROM_NEUTRAL;
		return this.data.cpmSessionDaily.lastDay.maxExtension;
	}
	currentMaxExtensionGoal() {
		if(!this.data || !this.data.doctorKneeProtocol || !this.data.cpmSessionDaily.lastDay) return '???';

		const percent = Math.floor((KNEE_ROM_NEUTRAL - this.data.cpmSessionDaily.lastDay.maxExtension) / (KNEE_ROM_NEUTRAL - this.data.doctorKneeProtocol.targetExtension) * 100);
		
		return Math.max(Math.min(percent, 100), 0).toString();
	}
	currentHomeExercise() {
		if(!this.data) return secondsToTimeString(0);
		const lastDay = this.data.lastDay;
		const v = [...this.data.exerciseResponses].find(v => v.answerDate === lastDay);
		return secondsToTimeString(v ? v.value : 0);
	}
	avgHomeExercise() {
		if(!this.data) return secondsToTimeString(0);
		//get the total time reported exercising
		const v = [...this.data.exerciseResponses].reduce((p, c) => {
			return c.value + p;
		}, 0);
		
		return secondsToTimeString(Math.round(v / (this.postOpDay() || 1)));
	}
	currentAlerts() {
		const events = [];
		if(this.data && this.data.cpmSessionDaily.lastDay) {
			const currentDate = this.data.cpmSessionDaily.lastDay.usageDate;
			events.push(...[...this.data.physicianPortalEvents].filter(physicianPortalEvent => physicianPortalEvent.eventDate === currentDate));
		}
		return events;
	}
	sortedAlerts() {
		const events = [];
		if(this.data) {
			events.push(...[...this.data.physicianPortalEvents].sort((a, b) => {
				if(a.physicianPortalEventDate < b.physicianPortalEventDate)
					return 1;
				if(a.physicianPortalEventDate > b.physicianPortalEventDate)
					return -1;
				return 0;
			}));
		}
		return events;
	}
	render() {
		const orderDetail = this.data ? this.data.orderDetail : null;
		const doctorKneeProtocol = this.data ? this.data.doctorKneeProtocol : null;

		return this.api.loggedIn ?
			html`
				<div>
					<div class="row buttons no-print">
						<button @click="${() => this.getPatientProgressPdf()}">
							<i class="icon fas fa-download"></i>
							Download
						</button>
					</div>
					<div class="row warning ${doctorKneeProtocol ? 'no-display' : ''}">
						<div>
							<i class="icon fas fa-exclamation-triangle"></i>
							<strong>No Matching Knee Protocol Found</strong>
							<div>
								Knee Protocols can be created from <a href="/my-account">My Account</a>.
							</div>
						</div>
					</div>
					<div class="row info">
						<div>
							<i class="icon fas fa-info-circle"></i>
							The report date shown reflects the most current set of data received by Kinex for this particular patient.
						</div>
					</div>
					<div class="row">
						<div class="patient-details card">
							<div><span class="name">Patient Name:</span>${orderDetail ? orderDetail.customerName : ''}</div>
							<div><span class="name">DOB:</span>${orderDetail ? toUTCLocaleDateString(new Date(orderDetail.customerDateOfBirth)) : ''}</div>
							<div><span class="name">Gender:</span>${orderDetail ? orderDetail.customerGender : ''}</div>
							<div><span class="name">Target Flexion:</span>${doctorKneeProtocol ? doctorKneeProtocol.targetFlexion : '???'}°</div>
							<div><span class="name">Target Extension:</span>${doctorKneeProtocol ? doctorKneeProtocol.targetExtension : '???'}°</div>
							<div><span class="name">CPM Rx:</span>${doctorKneeProtocol ? doctorKneeProtocol.durationText : '???'}</div>
						</div>
						<div class="physician-details card">
							<div><span class="name">Physician Name:</span>${orderDetail ? orderDetail.doctorName : ''}</div>
							<div><span class="name">Surgery Date:</span>${orderDetail ? toUTCLocaleDateString(new Date(orderDetail.surgeryDate)) : ''}</div>
							<div><span class="name">Report Date:</span>${this.reportDate()}</div>
							<div><span class="name">Post-Op Day:</span>${this.postOpDay()}</div>
							<div><span class="name">Limb:</span>${orderDetail ? orderDetail.limbName : ''}</div>
							<div><span class="name">ICD 10 Code:</span>${(orderDetail && orderDetail.icd10Code) ? orderDetail.icd10Code : '???'}</div>
						</div>
					</div>
					<!--<div class="section">
						<div class="row header">
							Overview
						</div>
						<div class="row">
							<div class="card">
								<div class="title">Post-Op Day</div>
								<div class="content">
									<div class="data-point">
										${this.postOpDay()}
									</div>
								</div>
							</div>
							<div class="card">
								<div class="title">Alerts</div>
								<div class="content">
									<div class="data-point">
										${this.currentAlerts().length}
										<div class="label">Current</div>
									</div>
								</div>
							</div>
							<div class="card">
								<div class="title">Usage Adherence</div>
								<div class="content">
									<div class="data-point">
										${this.currentUsageAdherence()}%
										<div class="label">Current</div>
									</div>
									<div class="data-point">
										${this.avgUsageAderence()}%
										<div class="label">Average</div>
									</div>
								</div>
							</div>
							<div class="card">
								<div class="title">Flexion</div>
								<div class="content">
									<div class="data-point">
										${this.currentMaxFlexion()}°
										<div class="label">Current</div>
									</div>
									<div class="data-point">
										${this.currentMaxFlexionGoal()}%
										<div class="label">% to Goal</div>
									</div>
								</div>
							</div>
							<div class="card">
								<div class="title">Extension</div>
								<div class="content">
									<div class="data-point">
										${this.currentMaxExtension()}°
										<div class="label">Current</div>
									</div>
									<div class="data-point">
										${this.currentMaxExtensionGoal()}%
										<div class="label">% to Goal</div>
									</div>
								</div>
							</div>
							<div class="card">
								<div class="title">Home Exercise</div>
								<div class="content">
									<div class="data-point">
										${this.currentHomeExercise()}
										<div class="label">Current</div>
									</div>
									<div class="data-point">
										${this.avgHomeExercise()}
										<div class="label">Average</div>
									</div>
								</div>
							</div>
						</div>
					</div>-->
					<div class="section">
						<div class="row header">Range Of Motion</div>
						<div class="row">
							<pp-rom-chart
								.data="${this.data || null}"
							></pp-rom-chart>
						</div>
					</div>
					<div class="section">
						<div class="row header">Program Adherence</div>
						<div class="row">
							<pp-cpm-usage-chart 
								.data="${this.data || null}"
							></pp-cpm-usage-chart>
						</div>
					</div>
					<div class="section">
						<div class="row header">Pain</div>
						<div class="row">
							<pp-pain-chart
								.data="${this.data || null}"
							></pp-pain-chart>
						</div>
					</div>
					<div class="section">
						<div class="row header">Exercise Time</div>
						<div class="row">
							<pp-exercise-chart
								.data="${this.data || null}"
							></pp-exercise-chart>
						</div>
					</div>
					<div class="section">
						<div class="row header">Walk Time</div>
						<div class="row">
							<pp-walk-chart
								.data="${this.data || null}"
							></pp-walk-chart>
						</div>
					</div>
					<div class="section">
						<div class="row header">Satisfaction</div>
						<div class="row">
							<pp-satisfaction-chart
								.data="${this.data || null}"
							></pp-satisfaction-chart>
						</div>
					</div>
					<div class="section">
						<div class="row header">Alert History</div>
						<div class="row">
							<pp-patient-alerts
								.data="${this.data || null}"
							></pp-patient-alerts>
						</div>
					</div>
				</div>
			` :
			html`
				<div>
					<h4>Patient Progress is unavailable.</h4>
					<p>You are currently logged out</p>
				</div>
			`;
	}
}

customElements.define('pp-patient-progress', PatientProgress);